import { Injectable, inject } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { API_CONFIG, ApiConfig, DEFAULT_API_CONFIG } from '../base/api-config';
import { ENSURE_FRESH_CREDENTIALS } from '../base/http-context.tokens';
import {
  nextSignalrRetryDelay,
  createSignalrLogger,
  waitForInFlightHubStart,
  buildSignalrHttpOptions,
} from './signalr-reconnect.util';

export interface InAppNotificationPushDto {
  id: string;
  marinaId: string;
  audience: string;
  customerId?: string | null;
  type: string;
  title: string;
  message: string;
  entityType?: string | null;
  entityId?: string | null;
  deepLink: string;
  createdAt: string;
  unreadCount?: number | null;
}

/**
 * SignalR client for in-app notifications. Connect once per authenticated session.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationsHubService {
  private connection: HubConnection | null = null;
  /** Single-flight so concurrent connect()/subscribe() callers wait for Connected. */
  private connectPromise: Promise<void> | null = null;
  /** Bumped on disconnect so an in-flight start is dropped after logout. */
  private connectionEpoch = 0;
  /** False after disconnect() until the next connect() — suppresses automatic reconnect. */
  private enabled = false;
  private readonly config: ApiConfig = inject(API_CONFIG, { optional: true }) ?? DEFAULT_API_CONFIG;
  /** Awaited before connecting — SignalR bypasses the HTTP interceptors entirely. */
  private readonly ensureFreshCredentials = inject(ENSURE_FRESH_CREDENTIALS, { optional: true });
  /** Desired marina groups (survive connection rebuilds until disconnect). */
  private subscribedMarinaIds = new Set<string>();
  private subscribedCustomerIds = new Set<string>();
  private createdHandlers: ((dto: InAppNotificationPushDto) => void)[] = [];
  private reconnectedHandlers: (() => void)[] = [];

  private get hubUrl(): string {
    const base = this.config.baseUrl.replace(/\/$/, '');
    return `${base}/hubs/notifications`;
  }

  async connect(): Promise<void> {
    this.enabled = true;
    if (this.isHubConnected()) return;
    if (this.connectPromise) {
      await this.connectPromise;
      if (this.isHubConnected() || !this.enabled) return;
    }

    this.connectPromise = this.startConnection();
    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  private async startConnection(): Promise<void> {
    if (this.isHubConnected()) return;

    const epoch = this.connectionEpoch;

    // Refresh first if the access token is spent. SignalR negotiates with its own HTTP client, so
    // nothing else will do this for us, and negotiating with a lapsed cookie means a 401 per hub and
    // a "Connection lost" dialog for what is really just a routine token refresh.
    await this.ensureFreshCredentials?.();
    if (!this.enabled || epoch !== this.connectionEpoch) return;

    const desiredMarinas = [...this.subscribedMarinaIds];
    const desiredCustomers = [...this.subscribedCustomerIds];

    if (this.connection) {
      const state = this.connection.state;
      if (state === HubConnectionState.Connected || state === HubConnectionState.Connecting) {
        return;
      }
      try {
        await this.connection.stop();
      } catch {
        // ignore teardown errors
      }
      this.connection = null;
    }
    if (!this.enabled || epoch !== this.connectionEpoch) return;

    const hub: HubConnection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, buildSignalrHttpOptions(this.ensureFreshCredentials))
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext): number | null =>
          nextSignalrRetryDelay(this.enabled && this.connection === hub, retryContext.previousRetryCount),
      })
      .configureLogging(createSignalrLogger(() => this.enabled))
      .build();

    hub.on('NotificationCreated', (dto: InAppNotificationPushDto) => {
      this.createdHandlers.forEach((h) => h(dto));
    });

    hub.onreconnected(async () => {
      if (!this.enabled || this.connection !== hub) return;
      await this.resubscribeAll();
      this.reconnectedHandlers.forEach((h) => {
        try {
          h();
        } catch (e) {
          console.warn('[NotificationsHubService] onReconnected handler failed', e);
        }
      });
    });

    this.connection = hub;

    try {
      await hub.start();
      if (!this.enabled || epoch !== this.connectionEpoch || this.connection !== hub) {
        await hub.stop().catch(() => {});
        if (this.connection === hub) {
          this.connection = null;
        }
        return;
      }
    } catch (err) {
      if (this.connection === hub) {
        this.connection = null;
      }
      if (!this.enabled || epoch !== this.connectionEpoch) {
        return;
      }
      throw err;
    }

    // Rejoin groups after a full connection rebuild (not only automatic reconnect).
    for (const marinaId of desiredMarinas) {
      try {
        await hub.invoke('SubscribeToMarinaNotifications', marinaId);
      } catch (e) {
        console.warn('[NotificationsHubService] Resubscribe marina failed', marinaId, e);
      }
    }
    for (const customerId of desiredCustomers) {
      try {
        await hub.invoke('SubscribeToCustomerNotifications', customerId);
      } catch (e) {
        console.warn('[NotificationsHubService] Resubscribe customer failed', customerId, e);
      }
    }
  }

  async disconnect(): Promise<void> {
    this.enabled = false;
    this.connectionEpoch += 1;
    const inFlight = this.connectPromise;
    const hub = this.connection;
    this.connection = null;
    this.subscribedMarinaIds.clear();
    this.subscribedCustomerIds.clear();
    await waitForInFlightHubStart(inFlight);
    if (this.connectPromise === inFlight) {
      this.connectPromise = null;
    }
    if (hub) {
      try {
        await hub.stop();
      } catch {
        // ignore teardown errors — logout must not wait on a dying socket
      }
    }
  }

  onNotificationCreated(handler: (dto: InAppNotificationPushDto) => void): () => void {
    this.createdHandlers.push(handler);
    return () => {
      this.createdHandlers = this.createdHandlers.filter((h) => h !== handler);
    };
  }

  /** Fires after automatic reconnect (and group resubscribe). */
  onReconnected(handler: () => void): () => void {
    this.reconnectedHandlers.push(handler);
    return () => {
      this.reconnectedHandlers = this.reconnectedHandlers.filter((h) => h !== handler);
    };
  }

  async subscribeToMarina(marinaId: string): Promise<void> {
    if (!this.enabled) return;
    await this.ensureConnected();
    // Always invoke — early-return on the set alone skips rejoin after a rebuilt connection.
    await this.connection!.invoke('SubscribeToMarinaNotifications', marinaId);
    this.subscribedMarinaIds.add(marinaId);
  }

  async unsubscribeFromMarina(marinaId: string): Promise<void> {
    if (!this.subscribedMarinaIds.has(marinaId)) return;
    if (this.isHubConnected()) {
      try {
        await this.connection!.invoke('UnsubscribeFromMarinaNotifications', marinaId);
      } catch (e) {
        console.warn('[NotificationsHubService] Unsubscribe marina failed', marinaId, e);
      }
    }
    this.subscribedMarinaIds.delete(marinaId);
  }

  async subscribeToCustomer(customerId: string): Promise<void> {
    if (!this.enabled) return;
    await this.ensureConnected();
    await this.connection!.invoke('SubscribeToCustomerNotifications', customerId);
    this.subscribedCustomerIds.add(customerId);
  }

  async unsubscribeFromCustomer(customerId: string): Promise<void> {
    if (!this.subscribedCustomerIds.has(customerId)) return;
    if (this.isHubConnected()) {
      try {
        await this.connection!.invoke('UnsubscribeFromCustomerNotifications', customerId);
      } catch (e) {
        console.warn('[NotificationsHubService] Unsubscribe customer failed', customerId, e);
      }
    }
    this.subscribedCustomerIds.delete(customerId);
  }

  private async resubscribeAll(): Promise<void> {
    for (const marinaId of this.subscribedMarinaIds) {
      try {
        await this.connection?.invoke('SubscribeToMarinaNotifications', marinaId);
      } catch (e) {
        console.warn('[NotificationsHubService] Resubscribe marina failed', marinaId, e);
      }
    }
    for (const customerId of this.subscribedCustomerIds) {
      try {
        await this.connection?.invoke('SubscribeToCustomerNotifications', customerId);
      } catch (e) {
        console.warn('[NotificationsHubService] Resubscribe customer failed', customerId, e);
      }
    }
  }

  private async ensureConnected(): Promise<void> {
    if (this.isHubConnected()) return;
    if (!this.enabled) {
      throw new Error('Notifications SignalR connection is stopped');
    }
    await this.connect();
    if (!this.isHubConnected()) {
      throw new Error('Notifications SignalR connection is not Connected');
    }
  }

  private isHubConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  get isConnected(): boolean {
    return this.isHubConnected();
  }
}
